import { renderRosterPage, placeInConsolePane, type RosterPageSpec } from './roster-page.js';
import { renderRosterDetailPage, type RosterDetailPageSpec } from './roster-detail-page.js';
import { renderPage } from '../content-source.js';
import type { OrgMetadata } from '../metadata-source.js';

/**
 * CAOS-785 — the Setup app's OBJECT MANAGER surface, now authored metadata for its two BROWSE
 * screens, matching the CAOS-762/CAOS-382 pattern `setup-sharing.ts` already established: this
 * module knows only the two component keys and the `/setup/objects[/<object>]` route shape,
 * fetches the authored `rosterPage`/`rosterDetailPage` component via the SAME generic
 * `/api/metadata/component-get` route every other Setup browse uses, and hands off to the shared,
 * app-agnostic renderers in `roster-page.ts` / `roster-detail-page.ts`.
 *
 * Field-Level Security is now REACHED from here (each row of the object detail's "Fields" table
 * links to `/setup/objects/<object>/fields/<field>` via the new `rowHref` on
 * `object_manager_detail`'s Fields table) rather than from its own former top-level Setup nav
 * entry, which is retired — matching Salesforce's own IA (Setup → Object Manager → an object →
 * Fields & Relationships, with FLS edited per-field there).
 *
 * CAOS-413 — a THIRD route joins them, and it is the same shape for the same reason:
 * `/setup/objects/<object>/new-field` draws `caos-pkg-setup`'s authored `field_editor` page and
 * tells it which object it was opened for. What that editor then does with the vocabulary is that
 * package's business and this module makes no claim about it; what this module gained is a page key
 * and a segment. The segment is `new-field` rather than `fields/new` because `new` is a legal field
 * API name and would otherwise shadow the access screen of a field called that.
 *
 * CAOS-798 — the per-field grant EDITOR is no longer host code either. It is now
 * `caos-pkg-setup`'s `field_level_security` component, placed on that package's own
 * `field_level_security` page, and this module's only remaining job for that route is to draw that
 * page and tell it which field it was opened for.
 *
 * It stayed here for two reasons, and both are gone. The declarative `rosterPage`/
 * `rosterDetailPage` vocabulary genuinely cannot express a checkbox grid with one Save across many
 * permission sets — that is still true, and it is why the piece is a `component` rather than more
 * roster metadata. The platform's sanctioned answer for exactly that case is a `component` placed
 * on a `page`, which could not work until CAOS-577 made a package's bundle code loadable and
 * CAOS-798 made a declared `urlParameter` input resolve. It works now.
 *
 * The route parameters are passed as `routeParams`, which fills every input the placed component's
 * manifest declared with `source: "urlParameter"` — for this page, the object and the field. The
 * component is told what it is editing; it does not read the URL, and this module does not know
 * what the component does with either name.
 */

export type Navigate = (href: string) => void;

export interface SetupObjectManagerContext {
  readonly meta: OrgMetadata;
  readonly pathname: string;
  readonly navigate?: Navigate;
  /**
   * CAOS-1809 — who is reading, keyed as a placed component's inputs name them. The field editor and
   * the field-access screen are both PAGES placing a piece, and both already pass `routeParams` for
   * `source: "urlParameter"`; this is the same forwarding for `source: "userContext"`.
   */
  readonly viewer?: Readonly<Record<string, string>>;
}

export interface MetaResponse {
  json(): Promise<unknown>;
}
export type MetaFetch = (path: string) => Promise<MetaResponse>;

const defaultMetaFetch: MetaFetch = (path) =>
  fetch(path, { credentials: 'same-origin' }) as unknown as Promise<MetaResponse>;

let metaFetch: MetaFetch = defaultMetaFetch;

/** Override the component-fetch seam (production wiring / deterministic tests). */
export function setSetupObjectManagerFetch(fn: MetaFetch): void {
  metaFetch = fn;
}

interface Envelope {
  ok?: boolean;
  data?: unknown;
}

/** GET one authored component by type + key, merging its `key` back onto the returned `body`. */
async function fetchComponent<T extends { key: string }>(type: string, key: string): Promise<T | null> {
  let env: Envelope;
  try {
    const res = await metaFetch(`/api/metadata/component-get?type=${type}&key=${encodeURIComponent(key)}`);
    env = (await res.json()) as Envelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  const envelope = env.data as { key?: unknown; body?: Record<string, unknown> } | null;
  if (!envelope || typeof envelope.key !== 'string' || !envelope.body) return null;
  return { ...envelope.body, key: envelope.key } as unknown as T;
}

/**
 * CAOS-798 — the authored page that draws one field's access, shipped by `caos-pkg-setup`. Named
 * here and resolved out of the org's own metadata, the same way every other contributed page is.
 */
const FIELD_SECURITY_PAGE = 'field_level_security';

/**
 * CAOS-413 — the authored page that creates a field, shipped by `caos-pkg-setup`, resolved the same
 * way. The one thing this module needs to be true of it is that the component it places declares an
 * `object_key` input with `source: "urlParameter"`, because that is what `routeParams` below fills;
 * the package's own tests hold its manifest to that, since they are the side that can see it.
 */
const FIELD_EDITOR_PAGE = 'field_editor';

/**
 * The segment that opens the field editor for an object.
 *
 * `new-field` rather than `fields/new`, and that is not a style choice: a field's API name matches
 * `^[a-z][a-z0-9_]*$`, so `new` is a legal field name and `/setup/objects/<o>/fields/new` would
 * shadow the access screen for a field actually called that. A segment that cannot collide with a
 * name is the only one that stays unambiguous as the org fills up.
 */
const NEW_FIELD_SEGMENT = 'new-field';

/** Not-available pane shown when the authored component itself could not be read from the org. */
function notAvailable(title: string): HTMLElement {
  const empty = document.createElement('caos-empty-state');
  empty.setAttribute('title', title);
  empty.textContent = 'The page definition could not be read from this org’s installed metadata.';
  return empty;
}

/**
 * Parse `/setup/objects[/<object>[/fields/<field>]]`. Unlike every sibling Setup module's
 * `detailKey` (which reads exactly one segment), Object Manager needs the FULL remainder — the
 * field editor route nests a second level below the object.
 */
function parseDetail(pathname: string): { object?: string; field?: string; newField?: boolean } {
  const segments = pathname.split('/').filter(Boolean); // ['setup', 'objects', <object>, 'fields', <field>]
  const object = segments[2];
  if (object === undefined) return {};
  if (segments[3] === 'fields' && segments[4] !== undefined) return { object, field: segments[4] };
  // Checked AFTER the field route and against a segment no field name can occupy, so the two can
  // never be read for one another whatever an org calls its fields.
  if (segments[3] === NEW_FIELD_SEGMENT) return { object, newField: true };
  return { object };
}

/**
 * Render the Object Manager surface. Paints a loading container immediately, then resolves the
 * route to the roster, the object detail, or (nested two levels deep) the field's own
 * Field-Level Security editor. Returns `{ root, done }` so a test can await the initial round trip.
 */
export function renderSetupObjectManager(ctx: SetupObjectManagerContext): { root: HTMLElement; done: Promise<void> } {
  const root = document.createElement('div');
  root.className = 'setup-pane';
  const loading = document.createElement('caos-empty-state');
  loading.setAttribute('title', 'Loading…');
  root.append(loading);

  const { object, field, newField } = parseDetail(ctx.pathname);

  const done = (async (): Promise<void> => {
    if (object !== undefined && newField === true) {
      const page = ctx.meta.pages.get(FIELD_EDITOR_PAGE);
      if (!page) {
        root.replaceChildren(notAvailable('The field editor is not available'));
        return;
      }
      root.replaceChildren(
        renderPage(page, {
          meta: ctx.meta,
          appLabel: 'Setup',
          // Only the object: this screen creates a field rather than opening one, so there is no
          // field to name yet. The placed component declares `object_key` with
          // `source: "urlParameter"` and takes it from here.
          routeParams: { object_key: object },
          // CAOS-1809 — and `source: "userContext"` from the surface context, the same way.
          ...(ctx.viewer ? { viewer: ctx.viewer } : {}),
        }),
      );
      return;
    }

    if (object !== undefined && field !== undefined) {
      const page = ctx.meta.pages.get(FIELD_SECURITY_PAGE);
      if (!page) {
        // The Setup package is installed (this route only exists because it contributed the nav),
        // but the page that draws field access is not in this org's metadata. Saying so beats an
        // empty pane: it names the one thing that would fix it.
        root.replaceChildren(notAvailable('Field-Level Security is not available'));
        return;
      }
      root.replaceChildren(
        renderPage(page, {
          meta: ctx.meta,
          appLabel: 'Setup',
          // What the route said this screen is for. The placed component's manifest decides which
          // of its inputs these fill, by declaring `source: "urlParameter"` on them.
          routeParams: { object_key: object, field_key: field },
          // CAOS-1809 — and `source: "userContext"` from the surface context, the same way.
          ...(ctx.viewer ? { viewer: ctx.viewer } : {}),
        }),
      );
      return;
    }

    if (object !== undefined) {
      const spec = await fetchComponent<RosterDetailPageSpec>('rosterDetailPage', 'object_manager_detail');
      if (!spec) {
        root.replaceChildren(notAvailable('Object Manager is not available'));
        return;
      }
      const { root: surface, done: renderDone } = renderRosterDetailPage(spec, object, {
        navigate: ctx.navigate,
        fetch: metaFetch,
      });
      placeInConsolePane(root, surface);
      await renderDone;
      return;
    }

    const spec = await fetchComponent<RosterPageSpec>('rosterPage', 'object_manager_list');
    if (!spec) {
      root.replaceChildren(notAvailable('Object Manager is not available'));
      return;
    }
    const { root: surface, done: renderDone } = renderRosterPage(spec, { navigate: ctx.navigate, fetch: metaFetch });
    placeInConsolePane(root, surface);
    await renderDone;
  })();

  return { root, done };
}
